// Inbox (F06, RF-GH-03): external deliveries (GitHub, Sentry) persisted before processing.
// GET /v1/inbox is gated to organization maintainers/admins (the database enforces the same
// gate); other roles get an honest explanation instead of an empty list. Filters map 1:1 to
// the API query; the detail drawer shows the stored delivery (allowlisted headers + payload,
// untrusted) and the two write actions: reprocess and assign to a project.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useI18n } from "../i18n/index.tsx";
import type { MessageKey } from "../i18n/en.ts";
import { assignInboxEvent, getInboxEvent, listInbox, listIntegrations, reprocessInboxEvent } from "../api/endpoints-wave3.ts";
import { INBOX_STATES, REQUEUEABLE_INBOX_STATES, type InboxEventDetail, type InboxEventSummary, type InboxState, type Integration } from "../api/types-wave3.ts";
import { useAsync } from "../lib/useAsync.ts";
import { useRealtimeEvents } from "../realtime/RealtimeProvider.tsx";
import { Button } from "../components/Button.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { SelectField } from "../components/Field.tsx";
import { Banner, EmptyState, ErrorBanner, LoadingBlock } from "../components/Feedback.tsx";
import { ActionError } from "../components/ActionError.tsx";
import { useToast } from "../components/Toast.tsx";
import { InboxIcon, LockIcon, RefreshIcon } from "../components/icons.tsx";
import { useOrgRole } from "../features/config/shared.tsx";
import { LoadMore, ShortId, StatePill, UntrustedBlock, useCursorList, useProjectOptions, useProjectRole, type ProjectOption } from "../features/wave3/common.tsx";

const PAGE = 50;

export const INBOX_STATE_VIEW: Record<InboxState, { tone: "success" | "danger" | "warning" | "info" | "neutral" | "running"; label: MessageKey }> = {
  received: { tone: "info", label: "inboxState.received" },
  processing: { tone: "running", label: "inboxState.processing" },
  processed: { tone: "success", label: "inboxState.processed" },
  failed: { tone: "danger", label: "inboxState.failed" },
  dead_letter: { tone: "danger", label: "inboxState.dead_letter" },
  ignored: { tone: "neutral", label: "inboxState.ignored" },
};

export function InboxPage() {
  const { t } = useI18n();
  const { role, atLeast } = useOrgRole();
  const allowed = atLeast("maintainer");
  return (
    <div className="page">
      <header className="page-header">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="page-title">{t("nav.inbox")}</h1>
          <p className="page-subtitle">{t("inbox.subtitle")}</p>
        </div>
      </header>
      {allowed ? (
        <InboxList />
      ) : (
        <div className="card" data-testid="inbox-role-gate">
          <EmptyState icon={<LockIcon />} title={t("inbox.roleTitle")}>
            <p>{t("inbox.roleBody", { role: role ? t(`role.${role}`) : "—" })}</p>
            <p className="subtle">{t("inbox.roleHint")}</p>
            <div className="empty-state-actions">
              <Link to="/integrations?tab=incidents" className="btn">
                {t("inbox.seeIncidents")}
              </Link>
            </div>
          </EmptyState>
        </div>
      )}
    </div>
  );
}

function InboxList() {
  const { t, formatDateTime, formatRelative } = useI18n();
  const [params, setParams] = useSearchParams();
  const state = (params.get("state") ?? "") as InboxState | "";
  const integrationId = params.get("integration") ?? "";
  const scope = params.get("project") ?? "";
  const projects = useProjectOptions();
  const integrations = useAsync((s) => listIntegrations(s).catch(() => ({ items: [] as Integration[] })), []);
  const [openId, setOpenId] = useState<string | null>(null);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const list = useCursorList<InboxEventSummary>(
    (cursor, signal) =>
      listInbox(
        {
          state,
          ...(integrationId ? { integration_id: integrationId } : {}),
          ...(scope === "unassigned" ? { unassigned: true } : scope ? { project_id: scope } : {}),
          cursor,
          limit: PAGE,
        },
        signal,
      ),
    [state, integrationId, scope],
  );

  useRealtimeEvents((e) => {
    if (e.type === "inbox.received") list.reload();
  }, null);

  const integrationName = (id: string) => integrations.data?.items.find((i) => i.id === id)?.name ?? null;
  const filtered = Boolean(state || integrationId || scope);

  return (
    <section className="card" aria-labelledby="inbox-list-title" data-testid="inbox-list">
      <div className="card-header">
        <InboxIcon size={18} />
        <h2 id="inbox-list-title" className="card-title" style={{ flex: 1 }}>
          {t("inbox.deliveries")}
        </h2>
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={list.reload}>
          {t("common.refresh")}
        </Button>
      </div>
      <div className="card-body stack">
        <div className="w3-toolbar" role="group" aria-label={t("inbox.filters")}>
          <SelectField label={t("inbox.filter.state")} value={state} onChange={(e) => setFilter("state", e.target.value)}>
            <option value="">{t("common.all")}</option>
            {INBOX_STATES.map((s) => (
              <option key={s} value={s}>
                {t(INBOX_STATE_VIEW[s].label)}
              </option>
            ))}
          </SelectField>
          <SelectField label={t("inbox.filter.integration")} value={integrationId} onChange={(e) => setFilter("integration", e.target.value)}>
            <option value="">{t("common.all")}</option>
            {(integrations.data?.items ?? []).map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({t(`integrationKind.${i.kind}`)})
              </option>
            ))}
          </SelectField>
          <SelectField label={t("inbox.filter.project")} value={scope} onChange={(e) => setFilter("project", e.target.value)}>
            <option value="">{t("inbox.filter.anyProject")}</option>
            <option value="unassigned">{t("inbox.filter.unassigned")}</option>
            {projects.options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </SelectField>
        </div>
        <p className="field-hint">{t("inbox.untrustedNote")}</p>
        {list.error && !list.items ? (
          <ErrorBanner error={list.error} onRetry={list.reload} title={t("inbox.loadFailed")} />
        ) : list.items === null ? (
          <LoadingBlock label={t("common.loading")} />
        ) : list.items.length === 0 ? (
          <EmptyState icon={<InboxIcon />} title={filtered ? t("inbox.emptyFilteredTitle") : t("inbox.emptyTitle")} headingLevel={3}>
            <p>{filtered ? t("inbox.emptyFilteredBody") : t("inbox.emptyBody")}</p>
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("inbox.deliveries")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("inbox.col.received")}</th>
                  <th scope="col">{t("inbox.col.event")}</th>
                  <th scope="col">{t("inbox.col.state")}</th>
                  <th scope="col">{t("inbox.col.project")}</th>
                  <th scope="col">{t("inbox.col.attempts")}</th>
                  <th scope="col">
                    <span className="sr-only">{t("common.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((ev) => {
                  const view = INBOX_STATE_VIEW[ev.state];
                  return (
                    <tr key={ev.id} data-testid="inbox-row" data-state={ev.state}>
                      <td>
                        <span title={formatDateTime(ev.received_at)}>{formatRelative(ev.received_at)}</span>
                      </td>
                      <td>
                        <span className="badge">{t(`inboxSource.${ev.source}`)}</span> <span className="mono">{ev.event_type}</span>
                        <span className="subtle" style={{ display: "block" }}>
                          {integrationName(ev.integration_id) ?? <ShortId id={ev.integration_id} />}
                        </span>
                      </td>
                      <td>
                        <StatePill tone={view.tone} label={t(view.label)} />
                        {ev.last_error ? (
                          <span className="subtle user-content" style={{ display: "block" }}>
                            {ev.last_error}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {ev.project_id ? (
                          <Link to={`/projects/${ev.project_id}`} className="link-subtle">
                            {projects.nameOf(ev.project_id) ?? <ShortId id={ev.project_id} />}
                          </Link>
                        ) : (
                          <span className="subtle">{t("inbox.unassigned")}</span>
                        )}
                      </td>
                      <td>
                        {ev.attempts}
                        {ev.reprocess_count > 0 ? <span className="subtle"> · {t("inbox.reprocessed", { n: ev.reprocess_count })}</span> : null}
                      </td>
                      <td>
                        <Button size="sm" variant="ghost" onClick={() => setOpenId(ev.id)} aria-label={t("inbox.openDetailFor", { event: ev.event_type, id: ev.delivery_id })}>
                          {t("inbox.details")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {list.moreError ? <ErrorBanner error={list.moreError} onRetry={() => void list.loadMore()} /> : null}
        <LoadMore cursor={list.cursor} busy={list.moreBusy} count={list.items?.length ?? 0} onMore={() => void list.loadMore()} />
      </div>
      <InboxDetailDrawer id={openId} projects={projects.options} integrationName={integrationName} onClose={() => setOpenId(null)} onChanged={list.reload} />
    </section>
  );
}

function InboxDetailDrawer({
  id,
  projects,
  integrationName,
  onClose,
  onChanged,
}: {
  id: string | null;
  projects: ProjectOption[];
  integrationName: (id: string) => string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  const { toast } = useToast();
  const [detail, setDetail] = useState<InboxEventDetail | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [busy, setBusy] = useState<"reprocess" | "assign" | null>(null);
  const [target, setTarget] = useState("");

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setDetail(null);
    setLoadError(null);
    setActionError(null);
    setTarget("");
    getInboxEvent(id, controller.signal)
      .then(setDetail)
      .catch((e: unknown) => {
        if (!controller.signal.aborted && !(e instanceof DOMException && e.name === "AbortError")) setLoadError(e);
      });
    return () => controller.abort();
  }, [id]);

  const act = async (kind: "reprocess" | "assign") => {
    if (!detail) return;
    setBusy(kind);
    setActionError(null);
    try {
      const next = kind === "reprocess" ? await reprocessInboxEvent(detail.id) : await assignInboxEvent(detail.id, target);
      setDetail(next);
      toast(kind === "reprocess" ? t("inbox.reprocessQueued") : t("inbox.assignQueued"), "success");
      onChanged();
    } catch (e) {
      setActionError(e);
    } finally {
      setBusy(null);
    }
  };

  const requeueable = detail ? REQUEUEABLE_INBOX_STATES.includes(detail.state) : false;
  const view = detail ? INBOX_STATE_VIEW[detail.state] : null;
  return (
    <Dialog open={id !== null} onClose={onClose} title={detail ? `${detail.event_type}` : t("inbox.detailTitle")} description={detail ? t("inbox.deliveryId", { id: detail.delivery_id }) : undefined} variant="side" busy={busy !== null} testId="inbox-drawer">
      {loadError ? (
        <ErrorBanner error={loadError} title={t("inbox.detailFailed")} />
      ) : !detail ? (
        <LoadingBlock label={t("common.loading")} />
      ) : (
        <div className="stack">
          <dl className="kv">
            <div>
              <dt>{t("inbox.col.state")}</dt>
              <dd>{view ? <StatePill tone={view.tone} label={t(view.label)} testId="inbox-detail-state" /> : null}</dd>
            </div>
            <div>
              <dt>{t("inbox.source")}</dt>
              <dd>
                {t(`inboxSource.${detail.source}`)} · {integrationName(detail.integration_id) ?? <ShortId id={detail.integration_id} />}
              </dd>
            </div>
            <div>
              <dt>{t("inbox.col.project")}</dt>
              <dd>{detail.project_id ? (projects.find((p) => p.id === detail.project_id)?.name ?? <ShortId id={detail.project_id} />) : t("inbox.unassigned")}</dd>
            </div>
            <div>
              <dt>{t("inbox.col.received")}</dt>
              <dd>{formatDateTime(detail.received_at)}</dd>
            </div>
            <div>
              <dt>{t("inbox.occurred")}</dt>
              <dd>{formatDateTime(detail.occurred_at)}</dd>
            </div>
            <div>
              <dt>{t("inbox.processed")}</dt>
              <dd>{formatDateTime(detail.processed_at)}</dd>
            </div>
            <div>
              <dt>{t("inbox.col.attempts")}</dt>
              <dd>
                {detail.attempts} · {t("inbox.reprocessed", { n: detail.reprocess_count })}
              </dd>
            </div>
            <div>
              <dt>{t("inbox.nextAttempt")}</dt>
              <dd>{detail.next_attempt_at ? formatDateTime(detail.next_attempt_at) : t("inbox.noNextAttempt")}</dd>
            </div>
            <div>
              <dt>{t("inbox.orderingKey")}</dt>
              <dd className="mono">{detail.ordering_key ?? "—"}</dd>
            </div>
            <div>
              <dt>{t("inbox.correlation")}</dt>
              <dd className="mono">{detail.correlation_id ?? "—"}</dd>
            </div>
            <div>
              <dt>{t("inbox.payloadHash")}</dt>
              <dd className="mono" style={{ overflowWrap: "anywhere" }}>
                {detail.payload_sha256}
              </dd>
            </div>
          </dl>
          {detail.last_error || detail.dead_letter_reason ? (
            <Banner tone="error" title={detail.dead_letter_reason ? t("inbox.deadLetter") : t("inbox.lastError")}>
              <p className="user-content">{detail.dead_letter_reason ?? detail.last_error}</p>
            </Banner>
          ) : null}

          <section className="stack" aria-labelledby="inbox-actions-title">
            <h3 id="inbox-actions-title" className="subheading">
              {t("common.actions")}
            </h3>
            <ActionError error={actionError} onDismiss={() => setActionError(null)} />
            <div className="row row--wrap">
              <Button onClick={() => void act("reprocess")} loading={busy === "reprocess"} disabled={!requeueable || busy !== null} aria-describedby="reprocess-hint">
                {t("inbox.reprocess")}
              </Button>
            </div>
            <p id="reprocess-hint" className="field-hint">
              {requeueable ? t("inbox.reprocessHint") : t("inbox.reprocessUnavailable", { state: view ? t(view.label) : detail.state })}
            </p>
            {detail.project_id === null ? (
              <AssignForm projects={projects} target={target} setTarget={setTarget} busy={busy === "assign"} disabled={busy !== null || !requeueable} onAssign={() => void act("assign")} />
            ) : null}
          </section>

          <details className="disclosure">
            <summary>{t("inbox.headers")}</summary>
            <UntrustedBlock value={detail.headers} label={t("inbox.headers")} />
          </details>
          <details className="disclosure" open>
            <summary>{t("inbox.payload")}</summary>
            <p className="field-hint">{t("inbox.untrustedNote")}</p>
            <UntrustedBlock value={detail.payload} label={t("inbox.payload")} />
          </details>
        </div>
      )}
    </Dialog>
  );
}

function AssignForm({
  projects,
  target,
  setTarget,
  busy,
  disabled,
  onAssign,
}: {
  projects: ProjectOption[];
  target: string;
  setTarget: (v: string) => void;
  busy: boolean;
  disabled: boolean;
  onAssign: () => void;
}) {
  const { t } = useI18n();
  const role = useProjectRole(target || null);
  const canAssign = target !== "" && role.atLeast("maintainer");
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (canAssign) onAssign();
      }}
      aria-label={t("inbox.assign")}
      data-testid="inbox-assign"
    >
      <SelectField label={t("inbox.assignTo")} value={target} onChange={(e) => setTarget(e.target.value)} hint={t("inbox.assignHint")}>
        <option value="">{t("inbox.chooseProject")}</option>
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </SelectField>
      {target && !role.atLeast("maintainer") ? <p className="field-hint tone-warning">{t("inbox.assignRole")}</p> : null}
      <div className="row">
        <Button type="submit" variant="primary" loading={busy} disabled={disabled || !canAssign}>
          {t("inbox.assign")}
        </Button>
      </div>
    </form>
  );
}
